import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import { GET_ADMIN_CONVERSATIONS } from '@/shared/queries/admin';
import { QueryResult } from '@/shared/types/generated';
import { green } from '@/shared/styles/colors';
import {
  EmailLink,
  EmptyState,
  ErrorState,
  FilterChips,
  Panel,
  Pill,
  RowCard,
  Small,
  Spread,
  Stack,
  Strong,
} from './AdminUI';
import { ago, plural, shortDate } from './format';
import {
  byStylistsFirst,
  isNew,
  isStale,
  isWaiting,
  roleName,
  STALE_DAYS,
} from './messages';

type Filter = 'waiting' | 'stale' | 'new';

const FILTERS: Record<
  Filter,
  { label: string; test: typeof isWaiting; empty: string }
> = {
  waiting: {
    label: 'Waiting 1–30 days',
    test: isWaiting,
    empty: 'Nobody has left a message unopened for more than a day.',
  },
  stale: {
    label: `Over ${STALE_DAYS} days`,
    test: isStale,
    empty: `No messages have sat unopened for over ${STALE_DAYS} days.`,
  },
  new: {
    label: 'Last 24 hours',
    test: isNew,
    empty: 'Nothing sent in the last day is still unopened.',
  },
};

const MessagesPanel = ({
  isDesktop,
  initialFilter,
}: {
  isDesktop: boolean;
  initialFilter?: string;
}) => {
  const [filter, setFilter] = useState<Filter>(
    initialFilter === 'stale' || initialFilter === 'new'
      ? initialFilter
      : 'waiting',
  );
  const { data, loading, error, refetch } = useQuery<
    QueryResult<'adminConversations'>
  >(GET_ADMIN_CONVERSATIONS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const all = useMemo(
    () => [...(data?.adminConversations ?? [])].sort(byStylistsFirst),
    [data],
  );
  const conversations = all.filter(FILTERS[filter].test);
  const countOf = (f: Filter) =>
    data ? all.filter(FILTERS[f].test).length : null;
  const staleCount = countOf('stale') ?? 0;

  return (
    <View>
      <Spread>
        <FilterChips
          selected={filter}
          onSelect={setFilter}
          options={(Object.keys(FILTERS) as Filter[]).map((value) => ({
            value,
            label: FILTERS[value].label,
            count: countOf(value),
          }))}
        />
        <Small>
          Stylists first, then oldest. We can see whether a message was opened,
          not what it says. Can be a few minutes behind.
        </Small>
      </Spread>

      {loading && !data ? (
        <ActivityIndicator
          size="large"
          color={green}
          style={{ marginTop: 80 }}
        />
      ) : error && !data ? (
        <ErrorState what="messages" onRetry={() => refetch()} />
      ) : conversations.length === 0 ? (
        <EmptyState
          title="Nothing here"
          body={
            filter === 'waiting' && staleCount > 0
              ? `${FILTERS.waiting.empty} ${plural(staleCount, 'older chat')} ${staleCount === 1 ? 'is' : 'are'} under “Over ${STALE_DAYS} days”.`
              : FILTERS[filter].empty
          }
        />
      ) : (
        <Panel>
          {conversations.map((conversation) => (
            <RowCard
              key={`${conversation.channelUrl}-${conversation.recipient.userId}`}
              isDesktop={isDesktop}
            >
              <Stack style={{ flex: 3 }}>
                <View
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                >
                  <Strong>{conversation.recipient.name}</Strong>
                  <Pill
                    text={roleName(conversation.recipient)}
                    tone="neutral"
                  />
                </View>
                <EmailLink email={conversation.recipient.email} />
              </Stack>
              <Stack style={{ flex: 3 }}>
                <Small style={{ color: green }}>
                  Hasn’t opened a message from {conversation.sender.name} (
                  {roleName(conversation.sender)})
                </Small>
                <EmailLink email={conversation.sender.email} />
              </Stack>
              <Stack
                style={{
                  flex: 1.2,
                  alignItems: isDesktop ? 'flex-end' : 'flex-start',
                }}
              >
                <Strong>{ago(conversation.lastMessageAt)}</Strong>
                <Small>Sent {shortDate(conversation.lastMessageAt)}</Small>
              </Stack>
            </RowCard>
          ))}
          {filter === 'waiting' && staleCount > 0 ? (
            <Small style={{ marginTop: 4 }}>
              {plural(staleCount, 'older chat')} under &ldquo;Over {STALE_DAYS}{' '}
              days&rdquo;.
            </Small>
          ) : null}
        </Panel>
      )}
    </View>
  );
};

export default MessagesPanel;
